import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, Warning, DownloadSimple, ArrowSquareOut, Info } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

// A2P 10DLC Registration Wizard — collects every field Twilio's Trust Hub +
// Campaign forms will ask for, then generates a copy-paste-ready PDF. Also
// shows live status of Brand / Messaging Service registrations from the
// operator's actual Twilio account.

const USE_CASES = [
  { value: "customer_care", label: "Customer Care", desc: "Recommended for restaurants — order status, wait times, waitlist updates" },
  { value: "mixed", label: "Mixed", desc: "Multiple message types (care + occasional promotions)" },
  { value: "account_notification", label: "Account Notification", desc: "Account updates + transactional confirmations" },
  { value: "delivery_notification", label: "Delivery Notification", desc: "Order/pickup status only" },
];

const OPT_IN_METHODS = [
  { value: "verbal", label: "Verbal (host asks caller during phone intake)" },
  { value: "web_form", label: "Web form (guest joins waitlist via /join link)" },
  { value: "keyword", label: "Keyword (guest texts JOIN to number)" },
  { value: "paper_form", label: "Paper form (physical sign-up card at host stand)" },
];

const REQUIRED = [
  "legal_business_name", "ein", "business_address_line1", "business_city",
  "business_state", "business_postal", "contact_first_name", "contact_last_name",
  "contact_email", "contact_phone", "campaign_use_case", "sample_message_1",
  "opt_in_method", "opt_in_description",
];

const FIELD_LABELS = {
  legal_business_name: "Legal business name",
  ein: "EIN (or SSN if sole prop)",
  business_address_line1: "Address line 1",
  business_city: "City",
  business_state: "State",
  business_postal: "Postal code",
  contact_first_name: "Contact first name",
  contact_last_name: "Contact last name",
  contact_email: "Contact email",
  contact_phone: "Contact phone",
  campaign_use_case: "Campaign use case",
  sample_message_1: "Sample message #1",
  opt_in_method: "Opt-in method",
  opt_in_description: "Opt-in description",
};

export default function DashA2PWizard() {
  const { business, refresh } = useAuth();
  const [tab, setTab] = useState(1);
  const [fields, setFields] = useState({});
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/twilio/a2p/status");
      setStatus(r.data);
      // Prefill with effective values (saved wins, but profile fields fill in the blanks).
      // First render or if user hasn't started editing, seed from effective.
      setFields((prev) => {
        // If user is actively editing (fields already populated), don't clobber their input on poll.
        const hasEdits = Object.keys(prev || {}).length > 0;
        return hasEdits ? prev : (r.data.effective || r.data.saved || {});
      });
    } catch (err) {
      // Background A2P status poll — non-fatal (endpoint may 401 before creds
      // are connected, or transient 5xx from Twilio). Keep the last-known status.
      console.debug("a2p/status poll failed:", err?.message);
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const setField = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.post("/twilio/a2p/save", fields);
      toast.success("Saved. Regenerate the PDF whenever you want an updated copy-paste kit.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/twilio/a2p/kit.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `a2p-kit-${business?.slug || "buzzerai"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("A2P kit downloaded — every field pre-formatted for Twilio.");
    } catch (e) { toast.error("PDF download failed — save first, then retry."); }
  };

  const completeness = status?.completeness ?? 0;
  const missing = status?.required_missing || [];
  const optedOut = business?.sms_opted_out === true;

  const optOut = async () => {
    if (!window.confirm("Skip SMS setup? You'll keep the AI voice receptionist, but guests won't get automatic text updates when their table is ready. You can turn this back on anytime.")) return;
    try {
      await api.patch("/settings", { sms_opted_out: true });
      toast.success("SMS setup skipped — Voice AI stays on. You can re-enable this anytime in Settings.");
      refresh && refresh();
    } catch { toast.error("Couldn't save — try again"); }
  };
  const optIn = async () => {
    try {
      await api.patch("/settings", { sms_opted_out: false });
      toast.success("SMS setup re-enabled.");
      refresh && refresh();
    } catch { toast.error("Couldn't save — try again"); }
  };

  return (
    <div data-testid="a2p-wizard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">SMS Compliance</div>
          <h1 className="text-3xl">A2P 10DLC registration</h1>
        </div>
        <Link to="/help/twilio-setup" className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="a2p-open-playbook">
          <Info size={14} color="#1E3A2F" weight="fill" /> Playbook & timeline
        </Link>
      </div>

      {optedOut ? (
        <div className="paper-card p-6 mb-6 bg-[#C2D1C1]/30 border-2 border-[#1E3A2F]/30" data-testid="a2p-opted-out">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1E3A2F] flex items-center justify-center flex-shrink-0"><Warning size={20} color="#F5C543" weight="fill" /></div>
            <div className="flex-1">
              <div className="font-heading font-extrabold text-[#121914] text-base">SMS setup skipped — Voice AI running solo</div>
              <p className="text-sm text-[#5C6B61] leading-snug mt-1">
                Your AI voice receptionist is <b>fully operational</b>. Guests calling in get answered, added to your waitlist, and offered a callback. What's missing right now: automatic "your table is ready" texts. You can turn SMS back on any time — everything below is saved so you won't start over.
              </p>
              <button onClick={optIn} className="btn-primary !py-1.5 !px-3 text-xs mt-3" data-testid="a2p-opt-in">Turn SMS setup back on</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Why this exists */}
          <div className="paper-card p-5 mb-4 bg-[#FEF3C7] border border-[#F59E0B]/40" data-testid="a2p-intro">
            <div className="flex items-start gap-3">
              <Warning size={22} color="#92400E" weight="fill" className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-extrabold text-[#78350F] text-base leading-snug">SMS registration is a 3–7 day approval — plan for it, don't fight it</div>
                <p className="text-sm text-[#78350F] leading-snug mt-1">
                  Your <b>Voice AI is already live</b> — this wizard is only for boosting SMS deliverability on US carriers (AT&T, T-Mobile, Verizon require it as of 2023). Fill the form once → download the PDF → paste each field into Twilio → wait for approval. That's the whole process.
                </p>
              </div>
            </div>
          </div>

          {/* Opt-out escape hatch */}
          <div className="paper-card p-4 mb-6 flex items-center gap-4" data-testid="a2p-opt-out-block">
            <div className="flex-1">
              <div className="font-heading font-extrabold text-sm text-[#121914]">Don't want to deal with SMS setup right now?</div>
              <p className="text-xs text-[#5C6B61] leading-snug mt-0.5">
                Skip it. Your voice AI keeps answering the phone, adding guests to the waitlist, and handling to-go inquiries. Guests just won't get an automatic text when their table's ready — the host can still text them manually from the dashboard.
              </p>
            </div>
            <button onClick={optOut} className="btn-ghost !py-1.5 !px-3 text-xs flex-shrink-0" data-testid="a2p-opt-out">Skip SMS setup for now</button>
          </div>
        </>
      )}

      {/* Progress + status */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="paper-card p-5">
          <div className="overline mb-2">Form completeness</div>
          <div className="text-3xl font-heading font-extrabold text-[#1E3A2F]" data-testid="a2p-completeness">{completeness}%</div>
          <div className="w-full bg-[#E1DFD5] h-2 rounded-full mt-3 overflow-hidden">
            <div className="bg-[#1E3A2F] h-full transition-all duration-500" style={{ width: `${completeness}%` }} />
          </div>
          <p className="text-xs text-[#5C6B61] mt-3 leading-snug">
            {missing.length === 0
              ? "All required fields filled. Download the PDF and start pasting."
              : `Still needed: ${missing.map((k) => FIELD_LABELS[k] || k).join(", ")}`}
          </p>
        </div>

        <div className="paper-card p-5">
          <div className="overline mb-2">Live Twilio status</div>
          {status?.twilio_error ? (
            <p className="text-xs text-[#A5382A] leading-snug" data-testid="a2p-twilio-err">Couldn't reach Twilio: {status.twilio_error}. Connect Twilio in AI Reception first.</p>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-[#5C6B61]">Brands registered</span>
                <span className="font-mono text-[#121914]" data-testid="a2p-brand-count">{status?.brands?.length ?? "—"}</span>
              </div>
              {(status?.brands || []).map((b) => (
                <div key={b.sid} className="text-xs text-[#5C6B61] pl-3 border-l-2 border-[#C2D1C1]">
                  {b.brand_type || "STANDARD"} · <span className={b.status === "APPROVED" || b.status === "VERIFIED" ? "text-[#1E3A2F] font-semibold" : "text-[#D96C5B] font-semibold"}>{b.status || "unknown"}</span>
                  {b.failure_reason && <div className="text-[#A5382A]">{b.failure_reason}</div>}
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-[#E1DFD5] pt-2 mt-2">
                <span className="text-[#5C6B61]">Messaging services</span>
                <span className="font-mono text-[#121914]" data-testid="a2p-svc-count">{status?.messaging_services?.length ?? "—"}</span>
              </div>
              {(status?.messaging_services || []).map((s) => (
                <div key={s.sid} className="text-xs text-[#5C6B61] pl-3 border-l-2 border-[#C2D1C1]">
                  {s.friendly_name || s.sid} {s.usecase && <>· <span className="text-[#1E3A2F]">{s.usecase}</span></>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-md border border-[#E1DFD5] overflow-hidden mb-4">
        {[
          { key: 1, label: "1 · Business profile" },
          { key: 2, label: "2 · Campaign" },
          { key: 3, label: "3 · Review & download" },
        ].map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  className={`text-sm px-4 py-2 ${tab === t.key ? "bg-[#1E3A2F] text-white" : "bg-white text-[#121914] hover:bg-[#F7F6F2]"}`}
                  data-testid={`a2p-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {tab === 1 && (
        <div className="paper-card p-6 space-y-4" data-testid="a2p-panel-1">
          <div className="p-3 rounded-lg bg-[#C2D1C1]/30 border border-[#1E3A2F]/20 text-xs text-[#121914] leading-snug" data-testid="a2p-reassurance">
            <b>Don't have your EIN or full legal address yet?</b> Every field on this page saves independently. Fill what you have, click <i>Save & come back</i> at the bottom, and we'll remember it. Only when you actually file with Twilio do you need everything at once.
          </div>
          <div>
            <label className="overline block mb-1">Legal business name*</label>
            <input className="input-field" placeholder="EXACT name on your EIN letter — e.g. Mario's Trattoria LLC" value={fields.legal_business_name || ""} onChange={(e) => setField("legal_business_name", e.target.value)} data-testid="a2p-legal-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Business type*</label>
              <select className="input-field" value={fields.business_type || ""} onChange={(e) => setField("business_type", e.target.value)} data-testid="a2p-biz-type">
                <option value="">Select…</option>
                <option value="sole_prop">Sole proprietorship</option>
                <option value="llc">LLC</option>
                <option value="corporation">Corporation</option>
                <option value="partnership">Partnership</option>
                <option value="non_profit">Non-profit</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-1">EIN <span className="text-[10px] text-[#5C6B61] normal-case">(or SSN if sole prop) — <b className="text-[#1E3A2F]">save this for later if you don't have it handy</b></span></label>
              <input className="input-field font-mono" placeholder="12-3456789" value={fields.ein || ""} onChange={(e) => setField("ein", e.target.value)} data-testid="a2p-ein" />
              <p className="text-[10px] text-[#5C6B61] mt-1">Twilio needs this eventually to verify your brand, but you can complete every other field first and come back for the EIN.</p>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Address line 1*</label>
            <input className="input-field" placeholder="123 Main St" value={fields.business_address_line1 || ""} onChange={(e) => setField("business_address_line1", e.target.value)} data-testid="a2p-addr1" />
          </div>
          <div>
            <label className="overline block mb-1">Address line 2</label>
            <input className="input-field" placeholder="Suite 200 (optional)" value={fields.business_address_line2 || ""} onChange={(e) => setField("business_address_line2", e.target.value)} data-testid="a2p-addr2" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="overline block mb-1">City*</label>
              <input className="input-field" value={fields.business_city || ""} onChange={(e) => setField("business_city", e.target.value)} data-testid="a2p-city" />
            </div>
            <div>
              <label className="overline block mb-1">State*</label>
              <input className="input-field" placeholder="CA" value={fields.business_state || ""} onChange={(e) => setField("business_state", e.target.value)} data-testid="a2p-state" />
            </div>
            <div>
              <label className="overline block mb-1">Postal*</label>
              <input className="input-field" placeholder="94103" value={fields.business_postal || ""} onChange={(e) => setField("business_postal", e.target.value)} data-testid="a2p-postal" />
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Website</label>
            <input className="input-field" placeholder="https://mariostrattoria.com" value={fields.website || ""} onChange={(e) => setField("website", e.target.value)} data-testid="a2p-website" />
            <p className="text-xs text-[#5C6B61] mt-1">Twilio requires the site to be publicly reachable and show your business name.</p>
          </div>
          <div className="border-t border-[#E1DFD5] pt-4">
            <div className="overline mb-3">Authorized contact person</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">First name*</label>
                <input className="input-field" value={fields.contact_first_name || ""} onChange={(e) => setField("contact_first_name", e.target.value)} data-testid="a2p-cfn" />
              </div>
              <div>
                <label className="overline block mb-1">Last name*</label>
                <input className="input-field" value={fields.contact_last_name || ""} onChange={(e) => setField("contact_last_name", e.target.value)} data-testid="a2p-cln" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="overline block mb-1">Email*</label>
                <input className="input-field" type="email" value={fields.contact_email || ""} onChange={(e) => setField("contact_email", e.target.value)} data-testid="a2p-cemail" />
              </div>
              <div>
                <label className="overline block mb-1">Phone*</label>
                <input className="input-field" placeholder="+15555550100" value={fields.contact_phone || ""} onChange={(e) => setField("contact_phone", e.target.value)} data-testid="a2p-cphone" />
              </div>
            </div>
            <div className="mt-3">
              <label className="overline block mb-1">Job title</label>
              <input className="input-field" placeholder="Owner, General Manager, etc." value={fields.contact_title || ""} onChange={(e) => setField("contact_title", e.target.value)} data-testid="a2p-ctitle" />
            </div>
          </div>
          <div className="pt-4 flex justify-between flex-wrap gap-2">
            <button className="btn-ghost" onClick={saveAll} disabled={saving} data-testid="a2p-save-1">{saving ? "Saving…" : "Save & come back"}</button>
            <button className="btn-primary" onClick={() => { saveAll(); setTab(2); }} data-testid="a2p-next-1">Save & continue →</button>
          </div>
        </div>
      )}

      {tab === 2 && (
        <div className="paper-card p-6 space-y-4" data-testid="a2p-panel-2">
          <div>
            <label className="overline block mb-1">Use case*</label>
            <div className="grid gap-2 mt-1">
              {USE_CASES.map((u) => (
                <label key={u.value} className={`paper-card p-3 flex items-start gap-3 cursor-pointer border-2 ${fields.campaign_use_case === u.value ? "border-[#1E3A2F]" : "border-transparent"}`}>
                  <input type="radio" name="use_case" value={u.value} checked={fields.campaign_use_case === u.value} onChange={() => setField("campaign_use_case", u.value)} className="mt-1" data-testid={`a2p-usecase-${u.value}`} />
                  <div>
                    <div className="font-heading font-extrabold text-sm">{u.label}</div>
                    <p className="text-xs text-[#5C6B61] leading-snug">{u.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Campaign description</label>
            <textarea className="input-field" rows={2} placeholder="Waitlist and table-ready notifications for restaurant guests. Guests opt in verbally or via web form at time of joining." value={fields.campaign_description || ""} onChange={(e) => setField("campaign_description", e.target.value)} data-testid="a2p-desc" />
          </div>
          <div>
            <label className="overline block mb-1">Sample message #1* <span className="text-[10px] normal-case text-[#5C6B61]">(exactly what a guest receives)</span></label>
            <textarea className="input-field" rows={2} placeholder="Hi Sarah, your table for 4 at Mario's Trattoria is ready. Please come to the host stand. Reply STOP to opt out." value={fields.sample_message_1 || ""} onChange={(e) => setField("sample_message_1", e.target.value)} data-testid="a2p-msg1" />
          </div>
          <div>
            <label className="overline block mb-1">Sample message #2</label>
            <textarea className="input-field" rows={2} value={fields.sample_message_2 || ""} onChange={(e) => setField("sample_message_2", e.target.value)} data-testid="a2p-msg2" />
          </div>
          <div>
            <label className="overline block mb-1">Sample message #3</label>
            <textarea className="input-field" rows={2} value={fields.sample_message_3 || ""} onChange={(e) => setField("sample_message_3", e.target.value)} data-testid="a2p-msg3" />
          </div>
          <div>
            <label className="overline block mb-1">Opt-in method*</label>
            <select className="input-field" value={fields.opt_in_method || ""} onChange={(e) => setField("opt_in_method", e.target.value)} data-testid="a2p-optin-method">
              <option value="">Select…</option>
              {OPT_IN_METHODS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Opt-in description* <span className="text-[10px] normal-case text-[#5C6B61]">(Twilio reads this carefully)</span></label>
            <textarea className="input-field" rows={3} placeholder="When a guest joins the waitlist via our web form or by giving their phone number to a host at check-in, they agree to receive one text when their table is ready. Consent language is displayed at time of signup and includes STOP-to-opt-out instructions." value={fields.opt_in_description || ""} onChange={(e) => setField("opt_in_description", e.target.value)} data-testid="a2p-optin-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">HELP response</label>
              <textarea className="input-field" rows={2} placeholder="For help, please call us at +1 555 555 0100." value={fields.help_response || ""} onChange={(e) => setField("help_response", e.target.value)} data-testid="a2p-help" />
            </div>
            <div>
              <label className="overline block mb-1">STOP response</label>
              <textarea className="input-field" rows={2} placeholder="You've been unsubscribed. Reply START to resubscribe." value={fields.stop_response || ""} onChange={(e) => setField("stop_response", e.target.value)} data-testid="a2p-stop" />
            </div>
          </div>
          <div className="pt-4 flex justify-between flex-wrap gap-2">
            <button className="btn-ghost" onClick={() => setTab(1)} data-testid="a2p-back-2">← Back</button>
            <div className="flex gap-2">
              <button className="btn-ghost" onClick={saveAll} disabled={saving} data-testid="a2p-save-2">{saving ? "Saving…" : "Save & come back"}</button>
              <button className="btn-primary" onClick={() => { saveAll(); setTab(3); }} data-testid="a2p-next-2">Save & continue →</button>
            </div>
          </div>
        </div>
      )}

      {tab === 3 && (
        <div className="space-y-4" data-testid="a2p-panel-3">
          <div className="paper-card p-6">
            <div className="overline mb-3">Ready to file with Twilio</div>
            <p className="text-sm text-[#121914] leading-relaxed mb-4">
              You've filled <b>{completeness}%</b> of what Twilio needs. Download the PDF below — every field is pre-formatted so you can paste it directly into Twilio's Trust Hub, Brand, and Campaign forms without retyping.
            </p>
            {completeness < 100 && (
              <div className="p-3 rounded bg-[#FEF3C7] border border-[#F59E0B]/40 text-xs text-[#78350F] mb-4">
                <b>Missing:</b> {missing.map((k) => FIELD_LABELS[k] || k).join(", ")}. You can still download and fill the rest directly in Twilio, but coming back here later will save you re-entering it.
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <button onClick={downloadPdf} className="btn-primary inline-flex items-center gap-2" data-testid="a2p-download-pdf">
                <DownloadSimple size={16} color="#fff" weight="fill" /> Download A2P kit (PDF)
              </button>
              <a href="https://console.twilio.com/us1/develop/trust-hub/customer-profiles" target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2" data-testid="a2p-open-trusthub">
                Open Twilio Trust Hub <ArrowSquareOut size={14} />
              </a>
              <button className="btn-ghost" onClick={saveAll} disabled={saving} data-testid="a2p-save-3">{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </div>

          <div className="paper-card p-6">
            <div className="overline mb-3">Filing order</div>
            <ol className="text-sm text-[#5C6B61] leading-relaxed list-decimal ml-5 space-y-2">
              <li><b className="text-[#121914]">Trust Hub → Customer Profile</b> — paste business fields from PDF section 1. Wait 1–3 days for review.</li>
              <li><b className="text-[#121914]">Register Brand</b> — most fields prefill from your Customer Profile. Wait 24–72 hours.</li>
              <li><b className="text-[#121914]">Register Campaign</b> — paste use case + samples + opt-in from PDF section 3. Wait 1–7 days.</li>
              <li><b className="text-[#121914]">Create Messaging Service</b> — link Campaign + phone number. Instant.</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
